import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Button, Label, Input, Card, CardContent } from '../components/ui'
import { Music, Search, Play, Pause, SkipForward, SkipBack, Plus, X, Menu } from 'lucide-react'

import useAudioEngine from '../hooks/useAudioEngine'
import useDebouncedSearch from '../hooks/useDebouncedSearch'
import toast from 'react-hot-toast'
import { getYouTubeSearchErrorMessage } from '../utils/searchErrors'
import LyricsDisplay from '../components/LyricsDisplay'
import ProcessingJobsStatus from '../components/ProcessingJobsStatus'
import AnimatedGradientBackground from '../components/AnimatedGradientBackground'
import QueueItemCard from '../components/QueueItemCard'
import PracticeAudioControls from '../components/PracticeAudioControls'
import StudioModeToggle from '../components/StudioModeToggle'
import TimelineEditor from '../components/studio/TimelineEditor'
// Note: Using a simplified search for practice mode
import { extractColorsFromImage, generateAnimatedBackgroundColors } from '../utils/colorExtractor'
import { songsAPI } from '../services/api'
import { convex, convexClient } from '../convexClient'
import { api, coerceSong } from '../lib/convexHelpers'
import { cn } from '../lib/utils'
import { getActiveWordLevelLineIndex } from '../utils/wordLevelPlayback'
import {
  isLyricsCleanupJob,
  loadCompletedPracticeSong,
  loadCompletedSubmittedSong,
  requestPracticeSong,
  shouldRefreshUrlPlaceholder,
} from '../utils/practiceLyricsCleanup'

const FEELING_BORING_BACKGROUND_KEY = 'practice-feeling-boring-background'
const LEGACY_WORK_SAFE_BACKGROUND_KEY = 'practice-work-safe-background'

const cloneLyricsArray = (lyricsArray) =>
  Array.isArray(lyricsArray) ? lyricsArray.map((lyric) => ({ ...lyric })) : []

const PracticePage = () => {
  // Local state management (no server sync needed)
  const [queue, setQueue] = useState([])
  const [currentSongIndex, setCurrentSongIndex] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentPosition, setCurrentPosition] = useState(0)
  const [pitchShift, setPitchShift] = useState(0)

  // UI state
  const [showSearchSection, setShowSearchSection] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [isSubmittingUrl, setIsSubmittingUrl] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [youtubeSearchResults, setYoutubeSearchResults] = useState([])
  const [isSearchingYouTube, setIsSearchingYouTube] = useState(false)
  const [processingJobs, setProcessingJobs] = useState(new Map())
  const [jobPollingInterval, setJobPollingInterval] = useState(null)
  const [currentLyricIndex, setCurrentLyricIndex] = useState(0)
  const [lyrics, setLyrics] = useState([])
  const [studioLyricsSnapshot, setStudioLyricsSnapshot] = useState([])
  const [lyricsReady, setLyricsReady] = useState(false)
  const [isQueueOpen, setIsQueueOpen] = useState(false)

  // Audio controls
  const [vocalVolume, setVocalVolume] = useState(0.25) // 0.0 to 2.0 (25% default)
  const [accompanimentVolume, setAccompanimentVolume] = useState(2.0) // 0.0 to 2.0 (200% default)
  const [drumsVolume, setDrumsVolume] = useState(2.0) // 0.0 to 2.0 (200% default)
  const [vocalMuted, setVocalMuted] = useState(false)
  const [accompanimentMuted, setAccompanimentMuted] = useState(false)
  const [drumsMuted, setDrumsMuted] = useState(false)
  const [pitchShiftEnabled, setPitchShiftEnabled] = useState(true)

  // Lyrics display options
  const [showLyrics, setShowLyrics] = useState(true)
  const [showTranslation, setShowTranslation] = useState(true)
  const [showRomanization, setShowRomanization] = useState(true)
  const [forceLineMode, setForceLineMode] = useState(true)

  // Visual settings
  const [backgroundBrightness, setBackgroundBrightness] = useState(100)
  const [animationPaused, setAnimationPaused] = useState(false)
  const [useSimpleBackground, setUseSimpleBackground] = useState(() => {
    if (typeof window === 'undefined') return false
    try {
      const storedPreference = window.localStorage.getItem(FEELING_BORING_BACKGROUND_KEY)
      if (storedPreference !== null) {
        return storedPreference === 'true'
      }
      const legacyPreference = window.localStorage.getItem(LEGACY_WORK_SAFE_BACKGROUND_KEY)
      if (legacyPreference !== null) {
        return legacyPreference === 'true'
      }
    } catch (error) {
      console.warn('Unable to access localStorage for background preference:', error)
    }
    return false
  })

  // Studio mode
  const [isStudioMode, setIsStudioMode] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [isSavingLyrics, setIsSavingLyrics] = useState(false)

  // Background colors
  const [gradientColors, setGradientColors] = useState([
    { r: 59, g: 130, b: 246 }, // Blue
    { r: 147, g: 51, b: 234 }  // Purple
  ])
  const [animatedBackgroundColors, setAnimatedBackgroundColors] = useState(null)
  const [colorsLoaded, setColorsLoaded] = useState(true)
  const extractedPaletteCacheRef = useRef(new Map())
  const colorExtractionRequestRef = useRef(0)

  const lyricsContainerRef = useRef(null)
  const timelineEditorRef = useRef(null)
  const progressBarRef = useRef(null)
  const processingJobsRef = useRef(processingJobs)
  const deferredCleanupSongsRef = useRef(new Map())
  const completingCleanupJobsRef = useRef(new Set())
  const [isScrubbing, setIsScrubbing] = useState(false)
  const [scrubTime, setScrubTime] = useState(0)
  const scrubWasPlayingRef = useRef(false)

  // Get current song
  const currentSong = queue[currentSongIndex] || null

  const currentSongId = currentSong?.id || null
  const currentSongYoutubeId = currentSong?.youtubeId || null
  const thumbnailUrl = currentSongYoutubeId
    ? `/uploads/songs/${currentSongYoutubeId}/thumbnail.webp`
    : null

  // Debug logging for current song
  useEffect(() => {
    console.log('🎵 Practice Page - Current song changed:', {
      currentSong: currentSong ? {
        title: currentSong.title,
        artist: currentSong.artist,
        duration: currentSong.duration,
        hasLyrics: currentSong.lyrics?.length || 0
      } : null,
      queueLength: queue.length,
      currentIndex: currentSongIndex
    })
  }, [currentSong, queue.length, currentSongIndex])

  // Audio engine hook
  const {
    pitchShifterRef,
    audioEngineReady,
    audioEngineError,
    audioTracksLoaded,
    audioLoading,
    audioError,
    currentTime,
    localIsPlaying,
    needsUserGesture,
    handlePlay: enginePlay,
    handlePause: enginePause,
    handleSeek,
    changePitchShiftEngine,
    setSongEndedCallback,
    handleUserGesture,
    setLyricsReadyChecker
  } = useAudioEngine(
    currentSong,
    pitchShift,
    vocalVolume,
    accompanimentVolume,
    vocalMuted,
    accompanimentMuted,
    drumsVolume,
    drumsMuted,
    currentSongIndex, // Use index as version
    isPlaying
  )

  const handlePlay = useCallback(async () => {
    if (needsUserGesture) {
      await handleUserGesture()
    }
    setIsPlaying(true)
  }, [needsUserGesture, handleUserGesture])

  const handlePause = useCallback(() => {
    setIsPlaying(false)
  }, [])

  // Debug logging for audio engine status
  useEffect(() => {
    console.log('🎵 Practice Page - Audio engine status:', {
      audioEngineReady,
      audioEngineError,
      audioTracksLoaded,
      audioLoading,
      audioError,
      needsUserGesture,
      localIsPlaying,
      currentTime
    })
  }, [audioEngineReady, audioEngineError, audioTracksLoaded, audioLoading, audioError, needsUserGesture, localIsPlaying, currentTime])

  // Persist background preference
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(
        FEELING_BORING_BACKGROUND_KEY,
        useSimpleBackground ? 'true' : 'false'
      )
      if (window.localStorage.getItem(LEGACY_WORK_SAFE_BACKGROUND_KEY) !== null) {
        window.localStorage.removeItem(LEGACY_WORK_SAFE_BACKGROUND_KEY)
      }
    } catch (error) {
      console.warn('Unable to store background preference:', error)
    }
  }, [useSimpleBackground])

  // Set up song ended callback for auto-advance
  useEffect(() => {
    if (audioEngineReady && setSongEndedCallback) {
      const handleSongEnded = async () => {
        console.log('🎵 Song ended - checking if we should auto-advance')

        // Check if there are more songs in the queue
        if (currentSongIndex < queue.length - 1) {
          console.log('🎵 Auto-advancing to next song')
          setCurrentSongIndex(currentSongIndex + 1)
          // Auto-play will be handled by the audio engine
          setIsPlaying(true)
        } else {
          console.log('🎵 No more songs in queue - stopping playback')
          setIsPlaying(false)
        }
      }

      setSongEndedCallback(handleSongEnded)

      return () => {
        setSongEndedCallback(null)
      }
    }
  }, [audioEngineReady, currentSongIndex, queue.length])

  // Add click listener for user gesture when needed (like ParticipantPage)
  useEffect(() => {
    if (needsUserGesture) {
      const handleClick = async () => {
        await handleUserGesture();
      };

      document.addEventListener('click', handleClick, { once: true });
      document.addEventListener('keydown', handleClick, { once: true });
      document.addEventListener('touchstart', handleClick, { once: true });

      return () => {
        document.removeEventListener('click', handleClick);
        document.removeEventListener('keydown', handleClick);
        document.removeEventListener('touchstart', handleClick);
      };
    }
  }, [needsUserGesture, handleUserGesture]);

  // Set up lyrics readiness checker
  useEffect(() => {
    if (setLyricsReadyChecker) {
      setLyricsReadyChecker(() => lyricsReady)
    }
  }, [setLyricsReadyChecker, lyricsReady])

  // Update lyrics when current song changes
  useEffect(() => {
    setLyricsReady(false)

    if (currentSong && Array.isArray(currentSong.lyrics)) {
      const processedLyrics = currentSong.lyrics.map(lyric => ({
        ...lyric,
        start_time: parseTimeToSeconds(lyric.start_time),
        end_time: parseTimeToSeconds(lyric.end_time)
      }))

      setLyrics(processedLyrics)
      setStudioLyricsSnapshot(cloneLyricsArray(processedLyrics))
      setCurrentLyricIndex(0)
      setLyricsReady(true)
    } else {
      setLyrics([])
      setStudioLyricsSnapshot([])
      setCurrentLyricIndex(0)
      setLyricsReady(true)
    }

    if (lyricsContainerRef.current) {
      lyricsContainerRef.current.scrollTo({
        top: 0,
        behavior: 'auto'
      })
    }
  }, [currentSong])

  // Forced line mode should always start from the top of the lyrics list.
  useEffect(() => {
    if (!forceLineMode) return

    setCurrentLyricIndex(0)

    if (lyricsContainerRef.current) {
      lyricsContainerRef.current.scrollTo({
        top: 0,
        behavior: 'auto'
      })
    }
  }, [forceLineMode, currentSong?.id])

  // Extract colors from thumbnail
  useEffect(() => {
    const requestId = ++colorExtractionRequestRef.current

    const applyPalette = (colors) => {
      setGradientColors(colors)
      setAnimatedBackgroundColors(generateAnimatedBackgroundColors(colors))
      setColorsLoaded(true)
    }

    const extractThumbnailColors = async () => {
      if (thumbnailUrl && currentSongYoutubeId) {
        const cached = extractedPaletteCacheRef.current.get(currentSongYoutubeId)
        if (cached) {
          applyPalette(cached)
          return
        }

        setColorsLoaded(false)

        try {
          const colors = await extractColorsFromImage(thumbnailUrl)
          if (requestId !== colorExtractionRequestRef.current) return
          extractedPaletteCacheRef.current.set(currentSongYoutubeId, colors)
          applyPalette(colors)
        } catch (error) {
          if (requestId !== colorExtractionRequestRef.current) return
          console.warn('Failed to extract colors, using defaults:', error)
          const defaultColors = [
            { r: 59, g: 130, b: 246 }, // Blue
            { r: 147, g: 51, b: 234 }  // Purple
          ]
          applyPalette(defaultColors)
        }
      } else {
        if (requestId !== colorExtractionRequestRef.current) return
        const defaultColors = [
          { r: 59, g: 130, b: 246 }, // Blue
          { r: 147, g: 51, b: 234 }  // Purple
        ]
        applyPalette(defaultColors)
      }
    }

    extractThumbnailColors()
  }, [currentSongId, currentSongYoutubeId, thumbnailUrl])

  // Lyrics sync with audio
  useEffect(() => {
    if (pitchShifterRef.current && lyrics.length > 0) {
      const originalCallback = pitchShifterRef.current.timeUpdateCallback
      const wordLevel = currentSong?.wordLevelTranscription
      const useWordLevelTiming = !forceLineMode && wordLevel && wordLevel.length > 0

      pitchShifterRef.current.setTimeUpdateCallback((currentTime) => {
        if (originalCallback) originalCallback(currentTime)

        if (!audioTracksLoaded) {
          if (currentLyricIndex !== 0) {
            setCurrentLyricIndex(0)
          }
          if (lyricsContainerRef.current && lyricsContainerRef.current.scrollTop > 1) {
            lyricsContainerRef.current.scrollTo({
              top: 0,
              behavior: 'auto'
            })
          }
          return
        }

        const animationOffset = 0.5
        const firstWordLine = useWordLevelTiming
          ? wordLevel.find(line => line.segments && line.segments.length > 0)
          : null
        const rawFirstLyricStart = firstWordLine?.segments?.[0]?.start_time ?? lyrics[0]?.start_time ?? 0
        const firstLyricStart = Number.isFinite(Number(rawFirstLyricStart)) ? Number(rawFirstLyricStart) : 0
        const preRollThreshold = Math.max(0, firstLyricStart - animationOffset)

        // Keep lyrics pinned to top before the first line starts.
        if (currentTime < preRollThreshold) {
          if (currentLyricIndex !== 0) {
            setCurrentLyricIndex(0)
          }
          if (lyricsContainerRef.current && lyricsContainerRef.current.scrollTop > 1) {
            lyricsContainerRef.current.scrollTo({
              top: 0,
              behavior: 'auto'
            })
          }
          return
        }

        let newIndex = -1

        if (useWordLevelTiming) {
          // Lines can overlap when backing vocals or the next phrase start early.
          // Follow the line whose first word started most recently.
          newIndex = getActiveWordLevelLineIndex(wordLevel, currentTime)
        } else {
          // Line-level mode: original behavior using line start times
          for (let i = 0; i < lyrics.length; i++) {
            const lyric = lyrics[i]
            const adjustedStartTime = lyric.start_time - animationOffset

            if (currentTime >= adjustedStartTime) {
              if (i === lyrics.length - 1) {
                newIndex = i
              } else {
                const nextLyric = lyrics[i + 1]
                const nextAdjustedStartTime = nextLyric.start_time - animationOffset
                if (currentTime < nextAdjustedStartTime) {
                  newIndex = i
                  break
                }
              }
            }
          }
        }

        if (newIndex !== -1 && newIndex !== currentLyricIndex) {
          setCurrentLyricIndex(newIndex)
          scrollToCurrentLyric(newIndex)
        }
      })

      return () => {
        if (pitchShifterRef.current && originalCallback) {
          pitchShifterRef.current.setTimeUpdateCallback(originalCallback)
        }
      }
    }
  }, [lyrics, currentLyricIndex, currentSong?.wordLevelTranscription, pitchShifterRef.current, forceLineMode, audioTracksLoaded])

  // Helper functions
  const parseTimeToSeconds = (timeValue) => {
    if (typeof timeValue === 'number') return timeValue

    if (typeof timeValue === 'string') {
      if (timeValue.includes(':')) {
        const parts = timeValue.split(':')
        if (parts.length === 2) {
          const minutes = parseInt(parts[0], 10)
          const seconds = parseFloat(parts[1])
          return minutes * 60 + seconds
        }
        if (parts.length === 3) {
          const hours = parseInt(parts[0], 10)
          const minutes = parseInt(parts[1], 10)
          const seconds = parseFloat(parts[2])
          return hours * 3600 + minutes * 60 + seconds
        }
      }

      const numericValue = parseFloat(timeValue)
      if (!isNaN(numericValue)) return numericValue
    }

    return 0
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const effectiveProgressTime = isScrubbing ? scrubTime : currentTime

  const getProgressPercentage = () => {
    if (!currentSong || !currentSong.duration) return 0
    const percentage = (effectiveProgressTime / currentSong.duration) * 100
    return Math.min(Math.max(percentage, 0), 100)
  }

  const getKeyDisplay = () => {
    if (!currentSong || !currentSong.originalKey) return ''

    const keys = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']
    const originalIndex = keys.indexOf(currentSong.originalKey)

    if (originalIndex === -1) return currentSong.originalKey

    const newIndex = (originalIndex + pitchShift + 12) % 12
    return keys[newIndex]
  }

  const scrollToCurrentLyric = (index) => {
    if (lyricsContainerRef.current) {
      const lyricElement = lyricsContainerRef.current.querySelector(`[data-lyric-index="${index}"]`)
      if (lyricElement) {
        if (index === 0) {
          lyricsContainerRef.current.scrollTo({
            top: 0,
            behavior: 'auto'
          })
          return
        }

        lyricElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        })
      }
    }
  }

  const handleLyricClick = async (lyric, index) => {
    if (isStudioMode && timelineEditorRef.current?.openLyricEditor) {
      timelineEditorRef.current.openLyricEditor(index)
      return
    }

    if (lyric.start_time !== undefined) {
      try {
        await handleSeek(lyric.start_time, isPlaying) // Use intent state
        setCurrentLyricIndex(index)
      } catch (error) {
        console.error('Failed to seek to lyric position:', error)
      }
    }
  }

  const updateScrubTimeFromClientX = useCallback((clientX) => {
    if (!progressBarRef.current || !currentSong?.duration) return 0

    const rect = progressBarRef.current.getBoundingClientRect()
    const clampedX = Math.min(Math.max(clientX - rect.left, 0), rect.width)
    const percentage = rect.width ? clampedX / rect.width : 0
    const newTime = percentage * currentSong.duration

    setScrubTime(newTime)
    return newTime
  }, [currentSong])

  const handleProgressPointerDown = useCallback((e) => {
    if (!currentSong || !currentSong.duration || audioError) return

    scrubWasPlayingRef.current = localIsPlaying
    setIsScrubbing(true)

    if (localIsPlaying) {
      handlePause()
    }

    if (progressBarRef.current?.setPointerCapture) {
      try {
        progressBarRef.current.setPointerCapture(e.pointerId)
      } catch (err) {
        console.warn('Failed to set pointer capture:', err)
      }
    }

    updateScrubTimeFromClientX(e.clientX)
  }, [audioError, currentSong, handlePause, localIsPlaying, updateScrubTimeFromClientX])

  const handleProgressPointerMove = useCallback((e) => {
    if (!isScrubbing) return
    updateScrubTimeFromClientX(e.clientX)
  }, [isScrubbing, updateScrubTimeFromClientX])

  const finalizeScrub = useCallback(async (clientX) => {
    if (!isScrubbing) return

    const targetTime = updateScrubTimeFromClientX(clientX)

    setIsScrubbing(false)

    if (!currentSong || !currentSong.duration || audioError) return

    try {
      await handleSeek(targetTime, scrubWasPlayingRef.current)
      setIsPlaying(scrubWasPlayingRef.current) // Sync intent after scrub
    } catch (error) {
      console.error('Failed to seek to position during scrub:', error)
    }
  }, [audioError, currentSong, handleSeek, isScrubbing, updateScrubTimeFromClientX])

  const handleProgressPointerUp = useCallback((e) => {
    if (progressBarRef.current?.hasPointerCapture?.(e.pointerId)) {
      try {
        progressBarRef.current.releasePointerCapture(e.pointerId)
      } catch (err) {
        console.warn('Failed to release pointer capture:', err)
      }
    }
    finalizeScrub(e.clientX)
  }, [finalizeScrub])

  const handleProgressPointerCancel = useCallback((e) => {
    if (progressBarRef.current?.hasPointerCapture?.(e.pointerId)) {
      try {
        progressBarRef.current.releasePointerCapture(e.pointerId)
      } catch (err) {
        console.warn('Failed to release pointer capture on cancel:', err)
      }
    }
    finalizeScrub(e.clientX || 0)
  }, [finalizeScrub])

  useEffect(() => {
    if (!isScrubbing) {
      setScrubTime(currentTime)
    }
  }, [currentTime, isScrubbing])

  useEffect(() => {
    if (!currentSong) {
      setScrubTime(0)
      setIsScrubbing(false)
    } else if (!isScrubbing) {
      setScrubTime(0)
    }
  }, [currentSong, isScrubbing])

  // Helper function to check if URL is YouTube
  const isYouTubeUrl = (url) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/
    return youtubeRegex.test(url)
  }

  // Search existing songs via Convex
  const searchExistingSongs = async (query) => {
    if (!query.trim()) {
      setSearchResults([])
      return
    }

    setIsSearching(true)
    try {
      const results = await convexClient.query(api.songs.search, { query })
      setSearchResults((results || []).map(coerceSong))
    } catch (error) {
      console.error('Failed to search songs:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  // Search YouTube
  const searchYouTube = async (query) => {
    if (!query.trim()) {
      setYoutubeSearchResults([])
      return
    }

    setIsSearchingYouTube(true)
    try {
      const results = await songsAPI.searchYouTube(query, 5)
      setYoutubeSearchResults(results || [])
    } catch (error) {
      console.error('Failed to search YouTube:', error)
      toast.error(getYouTubeSearchErrorMessage(error), { duration: 6000 })
      setYoutubeSearchResults([])
    } finally {
      setIsSearchingYouTube(false)
    }
  }

  // Add existing song to queue
  const addExistingSongToQueue = async (song) => {
    try {
      const cleanup = await requestPracticeSong({
        song,
        ensureCleanup: (songId) => convex.mutation(api.processingJobs.ensureLyricsCleanup, { songId })
      })

      if (!cleanup.ready) {
        deferredCleanupSongsRef.current.set(cleanup.result.jobId, {
          songId: cleanup.result.songId || song.id,
        })
        setProcessingJobs(prev => {
          const next = new Map(prev)
          next.set(cleanup.result.jobId, cleanup.job)
          return next
        })
        void songsAPI.wakeProcessingJob(cleanup.result.jobId).catch(error => {
          console.warn('Lyrics cleanup is queued, but the worker wake request failed:', error)
        })
        setSearchQuery('')
        setSearchResults([])
        setShowSearchSection(false)
        setTimeout(() => startJobPolling(), 0)
        return cleanup.result
      }

      // Convert API song format to our local queue format (camelCase / Convex-shaped)
      const newSong = {
        id: Date.now(),
        status: 'ready',
        title: song.title || 'Unknown Title',
        artist: song.artist || 'Unknown Artist',
        youtubeId: song.youtubeId,
        duration: song.duration,
        originalKey: song.originalKey,
        wordLevelTranscription: song.wordLevelTranscription,
        lyrics: song.lyrics || [],
        ...song
      }

      setQueue(prev => [...prev, newSong])

      // If this is the first song, start playing
      if (queue.length === 0) {
        setCurrentSongIndex(0)
      }

      setSearchQuery('')
      setSearchResults([])
      setShowSearchSection(false)
      return cleanup.result
    } catch (error) {
      console.error('Failed to add existing song to queue:', error)
    }
  }

  useEffect(() => {
    processingJobsRef.current = processingJobs
  }, [processingJobs])

  // Poll processing job status
  const pollProcessingJob = async (jobId) => {
    try {
      const jobStatus = await songsAPI.getProcessingJob(jobId)
      const deferredSong = deferredCleanupSongsRef.current.get(jobId)
      const isDeferredCleanup = Boolean(deferredSong)

      if (jobStatus.status === 'completed' && deferredSong && !completingCleanupJobsRef.current.has(jobId)) {
        completingCleanupJobsRef.current.add(jobId)
        try {
          const freshSong = coerceSong(await loadCompletedPracticeSong({
            jobStatus,
            deferredSong,
            getSong: (songId) => convexClient.query(api.songs.get, { id: songId })
          }))
          if (freshSong) {
            setQueue(prev => {
              if (prev.some(item => item.cleanupJobId === jobId)) return prev
              const wasEmpty = prev.length === 0
              if (wasEmpty) setCurrentSongIndex(0)
              return [...prev, {
                ...freshSong,
                id: freshSong.id,
                status: 'ready',
                cleanupJobId: jobId,
                lyrics: freshSong.lyrics || [],
              }]
            })
            deferredCleanupSongsRef.current.delete(jobId)

            setProcessingJobs(prev => {
              const next = new Map(prev)
              next.set(jobId, jobStatus)
              return next
            })
          }
        } catch (error) {
          console.error('Failed to load refreshed song:', error)
          setProcessingJobs(prev => {
            const next = new Map(prev)
            const existing = next.get(jobId) || {}
            next.set(jobId, {
              ...existing,
              ...jobStatus,
              status: 'processing',
              currentStage: 'cleaning_lyrics',
            })
            return next
          })
        } finally {
          completingCleanupJobsRef.current.delete(jobId)
        }
      } else if (!(jobStatus.status === 'completed' && deferredSong)) {
        setProcessingJobs(prev => {
          const next = new Map(prev)
          next.set(jobId, jobStatus)
          return next
        })
      }

      // If job is completed, update the queue with the processed song
      if (!isDeferredCleanup && jobStatus.status === 'completed' && jobStatus.song) {
        setQueue(prev => prev.map(song => {
          if (shouldRefreshUrlPlaceholder(song, jobStatus)) {
            return {
              ...song,
              title: jobStatus.song.title || song.title,
              artist: jobStatus.song.artist || song.artist,
              youtubeId: jobStatus.song.youtubeId || song.youtubeId,
              duration: jobStatus.song.duration,
              originalKey: jobStatus.song.originalKey,
              wordLevelTranscription: jobStatus.song.wordLevelTranscription,
              lyrics: jobStatus.song.lyrics || [],
              status: 'ready'
            }
          }
          return song
        }))

        // Remove completed job from tracking
        setProcessingJobs(prev => {
          const newJobs = new Map(prev)
          newJobs.delete(jobId)
          return newJobs
        })
      }

      return jobStatus
    } catch (error) {
      console.error('Failed to poll processing job:', error)
      return null
    }
  }

  // Start polling for active jobs
  const startJobPolling = () => {
    if (jobPollingInterval) return // Already polling

    const interval = setInterval(async () => {
      const activeJobs = Array.from(processingJobsRef.current.values())
        .filter((job) => ['pending', 'queued', 'processing'].includes(job.status))
        .map((job) => job.jobId)
      if (activeJobs.length === 0) {
        // No active jobs, stop polling
        clearInterval(interval)
        setJobPollingInterval(null)
        return
      }

      // Poll all active jobs
      await Promise.all(activeJobs.map(jobId => pollProcessingJob(jobId)))
    }, 2000) // Poll every 2 seconds

    setJobPollingInterval(interval)
  }

  const retryPracticeProcessingJob = async (jobId) => {
    try {
      const result = await songsAPI.retryProcessingJob(jobId)
      setProcessingJobs(prev => {
        const next = new Map(prev)
        const existing = next.get(jobId) || { jobId, id: jobId }
        next.set(jobId, {
          ...existing,
          ...result,
          jobId,
          status: 'pending',
          currentStage: result?.currentStage || existing.currentStage || 'cleaning_lyrics',
        })
        return next
      })
      setTimeout(() => startJobPolling(), 0)
    } catch (error) {
      console.error('Failed to retry processing job:', error)
      toast.error('Failed to retry lyrics refresh')
    }
  }

  // Local queue management
  const addToQueue = async (youtubeUrl) => {
    // Extract video title from URL for immediate display
    const extractVideoTitle = (url) => {
      try {
        const urlObj = new URL(url)
        const videoId = urlObj.searchParams.get('v') || urlObj.pathname.split('/').pop()
        return `YouTube Video ${videoId ? `(${videoId.substring(0, 6)}...)` : ''}`
      } catch {
        return 'YouTube Video'
      }
    }

    // Create a temporary job ID for immediate UI feedback
    const tempJobId = `temp_${Date.now()}`

    // Add processing job immediately for instant UI feedback
    setProcessingJobs(prev => {
      const newJobs = new Map(prev)
      newJobs.set(tempJobId, {
        id: tempJobId,
        jobId: tempJobId,
        status: 'pending',
        youtubeUrl,
        url: youtubeUrl,
        song: {
          title: extractVideoTitle(youtubeUrl),
          artist: 'Extracting...',
        },
        progress: 0,
        _creationTime: Date.now(),
      })
      return newJobs
    })

    setIsSubmittingUrl(true)
    try {
      const response = await songsAPI.submitYouTubeUrl(youtubeUrl, 'practice-session', null)

      if (response) {
        const completedSong = await loadCompletedSubmittedSong({
          response,
          getSong: (songId) => convexClient.query(api.songs.get, { id: songId })
        })

        if (completedSong) {
          const freshSong = coerceSong(completedSong)
          setProcessingJobs(prev => {
            const next = new Map(prev)
            next.delete(tempJobId)
            return next
          })
          setQueue(prev => {
            if (prev.length === 0) setCurrentSongIndex(0)
            return [...prev, {
              ...freshSong,
              status: 'ready',
              youtubeUrl: freshSong.youtubeUrl || youtubeUrl,
              lyrics: freshSong.lyrics || [],
            }]
          })
          setSearchQuery('')
          setShowSearchSection(false)
          return response
        }

        if (response.jobId) {
          setProcessingJobs(prev => {
            const newJobs = new Map(prev)
            newJobs.delete(tempJobId)
            newJobs.set(response.jobId, {
              id: response.jobId,
              jobId: response.jobId,
              status: response.status || 'pending',
              youtubeId: response.youtubeId,
              youtubeUrl,
              url: youtubeUrl,
              title: response.title,
              artist: response.artist,
              thumbnailUrl: response.thumbnailUrl,
              currentStage: response.currentStage,
              song: {
                title: response.title || extractVideoTitle(youtubeUrl),
                artist: response.artist || 'Processing...',
                youtubeId: response.youtubeId,
                thumbnailUrl: response.thumbnailUrl,
              },
              progress: response.progress || 0,
              _creationTime: Date.now(),
            })
            return newJobs
          })

          setTimeout(() => startJobPolling(), 1000)
        }

        if (isLyricsCleanupJob(response)) {
          deferredCleanupSongsRef.current.set(response.jobId, { songId: response.songId })
          setSearchQuery('')
          setShowSearchSection(false)
          return response
        }

        const newSong = {
          id: Date.now(),
          title: response.title || extractVideoTitle(youtubeUrl),
          artist: response.artist || 'Processing...',
          youtubeId: response.youtubeId,
          youtubeUrl: response.youtubeUrl || youtubeUrl,
          duration: response.duration,
          originalKey: response.originalKey,
          wordLevelTranscription: response.wordLevelTranscription,
          lyrics: response.lyrics || [],
          status: response.jobId ? 'processing' : 'ready',
          ...response,
        }

        setQueue(prev => [...prev, newSong])

        if (queue.length === 0) {
          setCurrentSongIndex(0)
        }

        setSearchQuery('')
        setShowSearchSection(false)
        return response
      }
    } catch (error) {
      console.error('Failed to add song to queue:', error)
      // Remove temporary job on error
      setProcessingJobs(prev => {
        const newJobs = new Map(prev)
        newJobs.delete(tempJobId)
        return newJobs
      })
      throw error
    } finally {
      setIsSubmittingUrl(false)
    }
  }

  const searchNow = useDebouncedSearch(
    searchQuery,
    showSearchSection && Boolean(searchQuery.trim()) && !isYouTubeUrl(searchQuery),
    (query) => Promise.all([searchExistingSongs(query), searchYouTube(query)])
  )

  const handleSearchSubmit = async (e) => {
    e.preventDefault()

    if (!searchQuery.trim()) return

    if (isYouTubeUrl(searchQuery)) {
      try {
        await addToQueue(searchQuery)
      } catch (error) {
        // Error handling could be added here
        console.error('Failed to add YouTube URL:', error)
      }
    } else {
      await searchNow()
    }
  }

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      setYoutubeSearchResults([])
    }
  }, [searchQuery])

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (jobPollingInterval) {
        clearInterval(jobPollingInterval)
      }
    }
  }, [jobPollingInterval])

  const removeFromQueue = (index) => {
    setQueue(prev => {
      const newQueue = prev.filter((_, i) => i !== index)

      // Adjust current song index if needed
      if (index < currentSongIndex) {
        setCurrentSongIndex(currentSongIndex - 1)
      } else if (index === currentSongIndex && newQueue.length > 0) {
        // If we removed the current song, play the next one (or first if we were at the end)
        const newIndex = Math.min(currentSongIndex, newQueue.length - 1)
        setCurrentSongIndex(newIndex)
      } else if (newQueue.length === 0) {
        setCurrentSongIndex(0)
      }

      return newQueue
    })
  }

  const playNext = () => {
    if (currentSongIndex < queue.length - 1) {
      setCurrentSongIndex(currentSongIndex + 1)
      setIsPlaying(true)
    }
  }

  const playPrevious = () => {
    if (currentSongIndex > 0) {
      setCurrentSongIndex(currentSongIndex - 1)
      setIsPlaying(true)
    }
  }

  const handlePitchShiftChange = (newPitchShift) => {
    setPitchShift(newPitchShift)
  }

  const handlePitchShiftEnabledChange = async (enabled) => {
    const changed = await changePitchShiftEngine(enabled ? 'signalsmith' : 'bypass')
    if (changed) {
      setPitchShiftEnabled(enabled)
    }
  }

  const handleLocalLyricChange = useCallback((updatedLyrics) => {
    if (!Array.isArray(updatedLyrics)) return

    setStudioLyricsSnapshot(cloneLyricsArray(updatedLyrics))

    const processedLyrics = updatedLyrics.map(lyric => ({
      ...lyric,
      start_time: parseTimeToSeconds(lyric.start_time),
      end_time: parseTimeToSeconds(lyric.end_time)
    }))

    setLyrics(processedLyrics)
  }, [])

  // Handle save lyrics
  const handleSaveLyrics = async (updatedLyrics) => {
    if (!currentSong || !currentSong.id) {
      console.error('No current song to save lyrics for')
      return
    }

    setIsSavingLyrics(true)

    try {
      await convex.mutation(api.songs.updateLyrics, { id: currentSong.id, lyrics: updatedLyrics })

      // Update local state
      setQueue(prev => prev.map(song =>
        song.id === currentSong.id
          ? { ...song, lyrics: updatedLyrics }
          : song
      ))

      // Update lyrics display
      const processedLyrics = updatedLyrics.map(lyric => ({
        ...lyric,
        start_time: parseTimeToSeconds(lyric.start_time),
        end_time: parseTimeToSeconds(lyric.end_time)
      }))
      setLyrics(processedLyrics)
      setStudioLyricsSnapshot(cloneLyricsArray(updatedLyrics))

      setHasUnsavedChanges(false)
      console.log('✅ Lyrics saved successfully')
    } catch (error) {
      console.error('Failed to save lyrics:', error)
      alert('Failed to save lyrics. Please try again.')
    } finally {
      setIsSavingLyrics(false)
    }
  }

  // Handle save word-level transcription
  const handleSaveWordLevel = async (updatedWordLevel) => {
    if (!currentSong || !currentSong.id) {
      console.error('No current song to save word-level transcription for')
      return
    }

    setIsSavingLyrics(true) // Reuse the same saving state

    try {
      await convex.mutation(api.songs.updateWordLevelTranscription, { id: currentSong.id, wordLevelTranscription: updatedWordLevel })

      // Update local state
      setQueue(prev => prev.map(song =>
        song.id === currentSong.id
          ? { ...song, wordLevelTranscription: updatedWordLevel }
          : song
      ))

      console.log('✅ Word-level transcription saved successfully')
    } catch (error) {
      console.error('Failed to save word-level transcription:', error)
      alert('Failed to save word-level transcription. Please try again.')
    } finally {
      setIsSavingLyrics(false)
    }
  }

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  useEffect(() => {
    if (!isQueueOpen) return

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsQueueOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isQueueOpen])

  useEffect(() => {
    if (!isQueueOpen) return

    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isQueueOpen])

  return (
    <div className="min-h-screen relative overflow-hidden bg-gray-900">
      {/* Background */}
      {useSimpleBackground ? (
        <div
          className="absolute inset-0 bg-gray-800 transition-all duration-300"
          style={{ opacity: backgroundBrightness / 100 }}
        />
      ) : (
        <AnimatedGradientBackground
          colors={animatedBackgroundColors}
          brightness={backgroundBrightness}
          animationPaused={animationPaused}
        />
      )}

      {/* User Gesture Required Overlay */}
      {needsUserGesture && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center">
            <div className="text-6xl mb-4">🎵</div>
            <h3 className="text-xl font-bold mb-2">Audio Ready to Play</h3>
            <p className="text-gray-600 mb-4">Click anywhere to enable audio playback</p>
            <p className="text-sm text-gray-500">This is required by your browser's audio policy</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="relative z-10 flex flex-col lg:flex-row h-screen min-h-0 min-w-0">
        {/* Left Side - TV Display */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0">
          {/* Header */}
          <div className="p-6">
            <div className="flex items-center gap-4">
              {/* Studio Mode Toggle */}
              {currentSong && (
                <StudioModeToggle
                  isActive={isStudioMode}
                  onToggle={() => {
                    if (hasUnsavedChanges) {
                      const confirmed = window.confirm(
                        'You have unsaved changes. Do you want to discard them?'
                      )
                      if (!confirmed) return
                    }
                    setIsStudioMode(!isStudioMode)
                    setHasUnsavedChanges(false)
                  }}
                  disabled={!currentSong || audioLoading}
                />
              )}
              <Button
                type="button"
                onClick={() => setIsQueueOpen(true)}
                variant="glass"
                size="lg"
                className="ml-auto shrink-0 rounded-full !bg-white/10 !border-white/20 text-white hover:!bg-white/20"
                aria-haspopup="dialog"
                aria-expanded={isQueueOpen}
                aria-controls="practice-queue-drawer"
              >
                <Menu className="h-5 w-5" />
                <span className="hidden sm:inline">Queue</span>
                {queue.length > 0 && (
                  <span className="ml-2 inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-blue-500 px-2 text-xs font-semibold text-white">
                    {queue.length}
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Song Display */}
          <div className="flex flex-col px-6 overflow-hidden flex-shrink min-h-0 min-w-0">
            {currentSong ? (
              <>
                {/* Song Info */}
                <div className="text-center mb-4 flex-shrink-0">
                  <h2 className="text-base lg:text-lg font-bold text-white mb-2">
                    {currentSong.title}
                  </h2>
                  <p className="text-sm lg:text-base text-white/80">
                    {currentSong.artist}
                  </p>
                </div>

                {/* Lyrics Display */}
                {isStudioMode ? (
                  /* Studio Mode: Side-by-side view for comparison (Line-level left, Word-level right) */
                  <div className="flex-1 min-h-0 overflow-hidden flex flex-row gap-4">
                    {/* Line-level lyrics (left) - editable */}
                    <div className="flex-1 min-w-0 overflow-hidden relative">
                      <div className="absolute top-0 left-0 bg-white/10 text-white/60 text-xs px-2 py-1 rounded-br-lg z-10">
                        Line-Level
                      </div>
                      <LyricsDisplay
                        lyrics={studioLyricsSnapshot.length > 0 ? studioLyricsSnapshot : lyrics}
                        currentLyricIndex={currentLyricIndex}
                        audioError={audioError}
                        audioLoading={audioLoading}
                        onLyricClick={handleLyricClick}
                        lyricsContainerRef={lyricsContainerRef}
                        formatTime={formatTime}
                        showLyrics={showLyrics}
                        showTranslation={showTranslation}
                        showRomanization={showRomanization}
                        isStudioMode={true}
                        currentTime={currentTime}
                        forceLineMode={true}
                      />
                    </div>

                    {/* Divider */}
                    <div className="w-px bg-white/20 flex-shrink-0" />

                    {/* Word-level lyrics (right) - read-only for comparison */}
                    <div className="flex-1 min-w-0 overflow-hidden relative">
                      <div className="absolute top-0 left-0 bg-white/10 text-white/60 text-xs px-2 py-1 rounded-br-lg z-10">
                        Word-Level (Read-only)
                      </div>
                      <LyricsDisplay
                        lyrics={lyrics}
                        currentLyricIndex={currentLyricIndex}
                        audioError={audioError}
                        audioLoading={audioLoading}
                        onLyricClick={handleLyricClick}
                        formatTime={formatTime}
                        showLyrics={showLyrics}
                        showTranslation={showTranslation}
                        showRomanization={showRomanization}
                        isStudioMode={false}
                        wordLevelTranscription={currentSong?.wordLevelTranscription}
                        currentTime={currentTime}
                        forceLineMode={false}
                      />
                    </div>
                  </div>
                ) : (
                  /* Regular Practice Mode: Single view (matches TVPage behavior) */
                  <div className="flex-1 min-h-0 overflow-hidden">
                    <LyricsDisplay
                      lyrics={lyrics}
                      currentLyricIndex={currentLyricIndex}
                      audioError={audioError}
                      audioLoading={audioLoading}
                      onLyricClick={handleLyricClick}
                      lyricsContainerRef={lyricsContainerRef}
                      formatTime={formatTime}
                      showLyrics={showLyrics}
                      showTranslation={showTranslation}
                      showRomanization={showRomanization}
                      isStudioMode={false}
                      wordLevelTranscription={currentSong?.wordLevelTranscription}
                      currentTime={currentTime}
                      forceLineMode={forceLineMode}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center min-h-[200px]">
                <div className="text-center">
                  <div className="text-8xl mb-4">🎤</div>
                  <h2 className="text-3xl font-bold text-white mb-2">Ready to Practice?</h2>
                  <p className="text-xl text-white/80">Add a song to get started</p>
                </div>
              </div>
            )}
          </div>

          {/* Playback Controls */}
          {currentSong && (
            <div
              className={cn(
                'p-6 flex-shrink-0',
                isStudioMode ? 'min-h-[400px] lg:min-h-[440px]' : 'min-h-[112px]'
              )}
            >
              {isStudioMode ? (
                /* Timeline Editor */
                <TimelineEditor
                  ref={timelineEditorRef}
                  song={currentSong}
                  initialLyrics={studioLyricsSnapshot.length > 0 ? studioLyricsSnapshot : lyrics}
                  resetKey={currentSong?.id || currentSong?.youtubeId}
                  wordLevelTranscription={currentSong?.wordLevelTranscription}
                  currentTime={currentTime}
                  onSeek={handleSeek}
                  onLyricsChange={handleLocalLyricChange}
                  onHasUnsavedChanges={setHasUnsavedChanges}
                  onSave={handleSaveLyrics}
                  onSaveWordLevel={handleSaveWordLevel}
                  isSaving={isSavingLyrics}
                  // Playback controls
                  isPlaying={localIsPlaying}
                  onPlay={handlePlay}
                  onPause={handlePause}
                  onSkipBack={playPrevious}
                  onSkipForward={playNext}
                  canSkipBack={currentSongIndex > 0}
                  canSkipForward={currentSongIndex < queue.length - 1}
                  audioEngineReady={audioEngineReady}
                  audioError={audioError}
                />
              ) : (
                /* Normal Progress Bar */
                <div className="bg-black/30 backdrop-blur-sm rounded-lg p-4">
                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div
                      ref={progressBarRef}
                      className="w-full bg-white/20 rounded-full h-2 cursor-pointer relative touch-none"
                      onPointerDown={handleProgressPointerDown}
                      onPointerMove={handleProgressPointerMove}
                      onPointerUp={handleProgressPointerUp}
                      onPointerCancel={handleProgressPointerCancel}
                    >
                      <div
                        className="bg-white rounded-full h-2 transition-none pointer-events-none"
                        style={{ width: `${getProgressPercentage()}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-sm text-white/60 mt-1">
                      <span>{formatTime(effectiveProgressTime)}</span>
                      <span>{formatTime(currentSong.duration || 0)}</span>
                    </div>
                  </div>

                  {/* Control Buttons */}
                  <div className="flex items-center justify-center space-x-4">
                    <Button
                      onClick={playPrevious}
                      disabled={currentSongIndex === 0}
                      variant="glass"
                      size="icon"
                    >
                      <SkipBack className="w-6 h-6" />
                    </Button>

                    <Button
                      onClick={localIsPlaying ? handlePause : handlePlay}
                      disabled={!currentSong || !audioEngineReady || !audioTracksLoaded || audioLoading || audioError}
                      variant="glass"
                      size="icon"
                      className="p-4 h-14 w-14"
                    >
                      {audioLoading ? (
                        <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : localIsPlaying ? (
                        <Pause className="w-8 h-8" />
                      ) : (
                        <Play className="w-8 h-8" />
                      )}
                    </Button>

                    <Button
                      onClick={playNext}
                      disabled={currentSongIndex >= queue.length - 1}
                      variant="glass"
                      size="icon"
                    >
                      <SkipForward className="w-6 h-6" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Queue Drawer Overlay */}
        {isQueueOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm transition-opacity duration-200 lg:hidden"
            onClick={() => setIsQueueOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Queue Drawer */}
        <div
          id="practice-queue-drawer"
          role="dialog"
          aria-label="Practice queue"
          className={`fixed inset-y-0 right-0 z-50 w-full max-w-md transform transition-transform duration-300 ease-out ${isQueueOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
        >
          <div className="flex h-full flex-col border-l border-white/10 bg-black/85 backdrop-blur-xl">
            <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold text-white">Queue</h2>
                <p className="text-xs text-white/60">Add, reorder, and manage practice songs</p>
              </div>
              <Button
                type="button"
                variant="glass"
                size="icon"
                className="rounded-full !bg-white/10 !border-white/20 text-white hover:!bg-white/20"
                onClick={() => setIsQueueOpen(false)}
                aria-label="Close queue"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Search Section */}
              <div className="border-b border-white/10 px-6 py-6">
                <Button
                  onClick={() => setShowSearchSection(!showSearchSection)}
                  variant="default"
                  size="lg"
                  className="flex w-full items-center justify-center space-x-2 !bg-blue-600 hover:!bg-blue-700"
                >
                  <Search className="h-5 w-5" />
                  <span>{showSearchSection ? 'Hide Search' : 'Search Songs'}</span>
                </Button>

                {/* Search Section - Collapsible */}
                {showSearchSection && (
                  <div className="mt-4 space-y-4">
                    {/* Search Input */}
                    <form onSubmit={handleSearchSubmit}>
                      <div className="relative">
                        <Input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search or paste YouTube URL..."
                          className="w-full border-white/20 bg-black/20 text-white placeholder-white/40"
                          autoFocus={showSearchSection}
                        />
                        {searchQuery && (
                          <button
                            type="button"
                            onClick={() => {
                              setSearchQuery('')
                              setSearchResults([])
                              setYoutubeSearchResults([])
                            }}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      {/* Helper text for YouTube URLs */}
                      {isYouTubeUrl(searchQuery) && (
                        <p className="mt-2 text-xs text-white/60">
                          YouTube URL detected - Press Enter to add
                        </p>
                      )}
                    </form>

                    {/* Search Results Container */}
                    {searchQuery.trim() && !isYouTubeUrl(searchQuery) && (
                      <div className="space-y-4 pr-1">
                        {/* Database Results Section */}
                        <div>
                          <h4 className="mb-2 text-sm font-medium text-white/80">
                            Available Songs
                          </h4>
                          {isSearching ? (
                            <div className="py-6 text-center">
                              <div className="mx-auto h-6 w-6 animate-spin rounded-full border-b-2 border-white" />
                              <p className="mt-2 text-sm text-white/60">Searching...</p>
                            </div>
                          ) : searchResults.length === 0 ? (
                            <div className="py-4 text-center text-white/60">
                              <Music className="mx-auto mb-2 h-8 w-8 opacity-50" />
                              <p className="text-sm">No songs found</p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {searchResults.map((song) => (
                                <Card
                                  key={song.id}
                                  className="cursor-pointer bg-black/40 p-3 text-white transition-colors hover:bg-black/60"
                                  onClick={() => addExistingSongToQueue(song)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="min-w-0 flex-1">
                                      <h5 className="truncate text-sm font-medium text-white">
                                        {song.title}
                                      </h5>
                                      <p className="truncate text-xs text-white/60">
                                        {song.artist}
                                      </p>
                                      <p className="text-xs text-white/40">
                                        {Math.floor(song.duration / 60)}:{String(song.duration % 60).padStart(2, '0')}
                                      </p>
                                    </div>
                                    <Plus className="ml-2 h-5 w-5 flex-shrink-0 text-white/60" />
                                  </div>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* YouTube Results Section */}
                        <div>
                          <h4 className="mb-2 flex items-center text-sm font-medium text-white/80">
                            <span>YouTube Results</span>
                            <span className="ml-2 text-xs font-normal text-red-400">(Requires Processing)</span>
                          </h4>
                          {isSearchingYouTube ? (
                            <div className="py-6 text-center">
                              <div className="mx-auto h-6 w-6 animate-spin rounded-full border-b-2 border-red-400" />
                              <p className="mt-2 text-sm text-white/60">Searching YouTube...</p>
                            </div>
                          ) : youtubeSearchResults.length === 0 ? (
                            <div className="py-4 text-center text-white/60">
                              <Music className="mx-auto mb-2 h-8 w-8 opacity-50" />
                              <p className="text-sm">No YouTube results</p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {youtubeSearchResults.map((video) => (
                                <Card
                                  key={video.youtubeId}
                                  className="cursor-pointer border-red-400/30 bg-black/40 p-3 text-white transition-colors hover:bg-black/60"
                                  onClick={() => !video.alreadyExists && addToQueue(video.youtubeUrl)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="min-w-0 flex-1">
                                      <div className="mb-1 flex items-center gap-2">
                                        <h5 className="truncate text-sm font-medium text-white">
                                          {video.title}
                                        </h5>
                                        {video.alreadyExists && (
                                          <span className="rounded bg-green-500/20 px-2 py-0.5 text-xs text-green-400">
                                            In Library
                                          </span>
                                        )}
                                      </div>
                                      <p className="truncate text-xs text-white/60">
                                        {video.artist}
                                      </p>
                                      <div className="flex items-center space-x-2 text-xs text-white/40">
                                        <span>{Math.floor(video.duration / 60)}:{String(video.duration % 60).padStart(2, '0')}
                                        </span>
                                        <span>•</span>
                                        <span className="text-red-400">YouTube</span>
                                      </div>
                                    </div>
                                    <Plus
                                      className={`ml-2 h-5 w-5 flex-shrink-0 ${video.alreadyExists ? 'text-white/20' : 'text-white/60'
                                        }`}
                                    />
                                  </div>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Processing Jobs Status */}
              {processingJobs.size > 0 && (
                <div className="border-b border-white/10 px-6 py-6">
                  <ProcessingJobsStatus
                    userActiveJobs={Array.from(processingJobs.values()).filter(
                      (job) => ['processing', 'pending', 'queued', 'failed'].includes(job.status)
                    )}
                    isDarkMode={true}
                    isPracticePage={true}
                    onRetry={retryPracticeProcessingJob}
                  />
                </div>
              )}

              {/* Queue */}
              <div className="px-6 py-6 pb-32">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Queue</h3>
                  <span className="rounded-full bg-white/10 px-2 py-0.5 text-xs text-white/70">
                    {queue.length} song{queue.length === 1 ? '' : 's'}
                  </span>
                </div>

                {queue.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-white/20 py-8 text-center text-white/60">
                    <Music className="mx-auto mb-3 h-12 w-12 opacity-50" />
                    <p>No songs in queue</p>
                    <p className="text-sm">Add a song to get started</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {queue.map((song, index) => (
                      <QueueItemCard
                        key={song.id}
                        item={{
                          id: song.id,
                          song: {
                            title: song.title,
                            artist: song.artist,
                            youtubeId: song.youtubeId,
                          },
                          status:
                            song.status === 'processing'
                              ? 'queued'
                              : song.status === 'ready' && index === currentSongIndex
                                ? 'playing'
                                : 'queued'
                        }}
                        isDarkMode={true}
                        handleRemoveFromQueue={() => removeFromQueue(index)}
                        showDragHandle={false}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Practice Audio Controls */}
      <PracticeAudioControls
        pitchShift={pitchShift}
        onPitchShiftChange={handlePitchShiftChange}
        pitchShiftEnabled={pitchShiftEnabled}
        onPitchShiftEnabledChange={handlePitchShiftEnabledChange}
        getKeyDisplay={getKeyDisplay}
        vocalVolume={vocalVolume}
        vocalMuted={vocalMuted}
        accompanimentVolume={accompanimentVolume}
        accompanimentMuted={accompanimentMuted}
        drumsVolume={drumsVolume}
        drumsMuted={drumsMuted}
        onVocalVolumeChange={setVocalVolume}
        onVocalMuteToggle={() => setVocalMuted(!vocalMuted)}
        onAccompanimentVolumeChange={setAccompanimentVolume}
        onAccompanimentMuteToggle={() => setAccompanimentMuted(!accompanimentMuted)}
        onDrumsVolumeChange={setDrumsVolume}
        onDrumsMuteToggle={() => setDrumsMuted(!drumsMuted)}
        showLyrics={showLyrics}
        showTranslation={showTranslation}
        showRomanization={showRomanization}
        onShowLyricsToggle={() => setShowLyrics(!showLyrics)}
        onShowTranslationToggle={() => setShowTranslation(!showTranslation)}
        onShowRomanizationToggle={() => setShowRomanization(!showRomanization)}
        forceLineMode={forceLineMode}
        onForceLineModeToggle={() => setForceLineMode(!forceLineMode)}
        isStudioMode={isStudioMode}
        backgroundBrightness={backgroundBrightness}
        animationPaused={animationPaused}
        useSimpleBackground={useSimpleBackground}
        onBackgroundBrightnessChange={setBackgroundBrightness}
        onAnimationPausedToggle={() => setAnimationPaused(!animationPaused)}
        onSimpleBackgroundToggle={() => setUseSimpleBackground(!useSimpleBackground)}
        audioEngineReady={audioEngineReady}
        audioEngineError={audioEngineError}
      />

    </div>
  )
}

export default PracticePage 
